import { type Rule, RuleMode } from '@cobliteam/api-lib/src/dashboard/alerts/definitions/rules';
import type { UUID } from '@cobliteam/common-types';
import { useGetRules } from '@cobliteam/hooks/src/useEntity/useAlerts';
import { useTranslation } from '@cobliteam/i18n';
import { Button } from '@cobliteam/ui/components';
import Dropdown from '@cobliteam/ui/components/Dropdown';
import Checkbox from '@cobliteam/ui/form/Checkbox';
import Label from '@cobliteam/ui/form/Label';

import Icon from '@cobliteam/ui/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';

interface MultiSelectRuleConditionsProps {
  selectedConditions: UUID[];
  selectedRule?: Rule;
  size?: 'sm' | 'md';
  isOpen?: boolean;
  onChange: (conditions: UUID[]) => void;
}

const MultiSelectRuleConditions = ({ selectedConditions, selectedRule, size = 'md', isOpen, onChange }: MultiSelectRuleConditionsProps) => {
  const { t } = useTranslation('multiselects');
  const { data: rules } = useGetRules(RuleMode.FLEET_POLICY);
  const conditions = useMemo(() => rules?.find((rule) => rule.id === selectedRule?.id)?.conditions || [], [rules, selectedRule]);

  const [forceClosed, setForceClosed] = useState(false);
  const [draft, setDraft] = useState(selectedConditions);

  const hasSameElements = selectedConditions?.length === draft?.length && selectedConditions.every((value) => draft.includes(value));

  const handleClick = useCallback(() => {
    setDraft(selectedConditions || []);
  }, [selectedConditions]);

  const handleClose = useCallback(() => {
    setForceClosed(true);
    setDraft([]);
  }, []);

  const handleApply = useCallback(() => {
    onChange(draft);
    handleClose();
  }, [draft, handleClose, onChange]);

  const handleSelect = useCallback(
    (conditionId: UUID) => {
      const newDraft = draft.includes(conditionId) ? draft.filter((d) => d !== conditionId) : [...draft, conditionId];
      setDraft(newDraft);
    },
    [draft, setDraft]
  );

  useEffect(() => setForceClosed(!isOpen), [isOpen]);

  useEffect(() => {
    if (forceClosed) setForceClosed(false);
  }, [forceClosed]);

  return (
    <Dropdown
      onOpenChange={handleClick}
      forceClose={forceClosed}
      portal={false}
      overlay={
        <Dropdown.Overlay>
          <Dropdown.Header className='px-2'>
            <Button variant='ghost' size='sm' disabled={draft?.length === 0} onClick={() => setDraft([])}>
              {t('rule_conditions.clear_selection')}
            </Button>
          </Dropdown.Header>
          <Dropdown.Body className='px-2'>
            <Dropdown.List
              heightList={300}
              heightItem={28}
              data={conditions}
              itemComponent={(item) => (
                <div className='flex items-center gap-2'>
                  <Checkbox id={item.id} key={item.id} checked={draft.some((d) => d === item.id)} onCheckedChange={() => handleSelect(item.id)} />
                  <Label htmlFor={item.id} className='line-clamp-1' title={item.name}>
                    {item.name}
                  </Label>
                </div>
              )}
            />
          </Dropdown.Body>
          <Dropdown.Footer>
            <Dropdown.Actions className='px-2'>
              <Button variant='ghost' full size='sm' onClick={handleClose}>
                {t('rule_conditions.cancel')}
              </Button>
              <Button size='sm' full onClick={handleApply} disabled={hasSameElements}>
                {t('rule_conditions.apply')}
              </Button>
            </Dropdown.Actions>
          </Dropdown.Footer>
        </Dropdown.Overlay>
      }
    >
      <Button size={size} variant='ghost' startIcon={<Icon name='filter' />}>
        {t('rule_conditions.title')} {(selectedConditions?.length || 0) > 0 ? `(${selectedConditions?.length})` : ''}
      </Button>
    </Dropdown>
  );
};

export default MultiSelectRuleConditions;
